import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useVault } from "@/context/VaultContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { UnlockScreen } from "@/components/passord/UnlockScreen";
import { EntrySheet } from "@/components/passord/EntrySheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { analyzeVault, ISSUE_META } from "@/lib/passwordHealth";
import { formatBreachCount } from "@/lib/breachCheck";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { HeartPulse, ShieldAlert, Repeat, CalendarClock, KeyRound, ShieldCheck, ArrowRight, Lock, Radar, Loader2, RefreshCw, Settings2, AlertTriangle } from "lucide-react";

const hostOf = (url) => {
  try { return new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`).hostname.replace(/^www\./, ""); } catch { return url || ""; }
};

const TONE = {
  success: "text-success",
  primary: "text-primary",
  warn: "text-warn",
  flag: "text-flag",
};

/** Ring som viser helsescore 0–100 (kun SVG, ingen eksterne avhengigheter). */
const ScoreRing = ({ score, tone, label }) => {
  const r = 44;
  const c = 2 * Math.PI * r;
  const off = c - (Math.max(0, Math.min(100, score)) / 100) * c;
  return (
    <div className="flex items-center gap-5" data-testid="health-score">
      <div className="relative h-28 w-28 shrink-0">
        <svg viewBox="0 0 100 100" className="h-28 w-28 -rotate-90">
          <circle cx="50" cy="50" r={r} className="stroke-border" strokeWidth="8" fill="none" />
          <circle cx="50" cy="50" r={r} className={cn("stroke-current transition-[stroke-dashoffset] duration-500", TONE[tone])} strokeWidth="8" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-semibold tnum" data-testid="health-score-value">{score}</span>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">av 100</span>
        </div>
      </div>
      <div>
        <p className={cn("text-lg font-semibold", TONE[tone])} data-testid="health-score-label">{label}</p>
        <p className="text-sm text-muted-foreground max-w-sm">Analysen kjører kun i nettleseren din. Lekkasjesjekken sender bare de 5 første tegnene av en hash – aldri passordet.</p>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, hint, active, onClick, testId, tone }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={cn("card-lift text-left rounded-xl border bg-card p-4 transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", active && "border-primary bg-accent/40")}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Icon className={cn("h-4 w-4", value > 0 ? tone : "text-muted-foreground")} />
    </div>
    <p className="mt-1 text-2xl font-semibold tnum">{value}</p>
    <p className="text-xs text-muted-foreground">{hint}</p>
  </button>
);

/** Statuslinje for lekkasjesjekken (HIBP k-anonymitet). */
const BreachStatus = ({ breach, onRerun }) => {
  const base = "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border px-4 py-3 text-sm";
  if (breach.status === "disabled") {
    return (
      <div className={cn(base, "bg-secondary/40")} data-testid="breach-status" data-state="disabled">
        <span className="flex items-center gap-2 text-muted-foreground"><Radar className="h-4 w-4" /> Lekkasjesjekk er slått av. Passordene sjekkes ikke mot kjente datalekkasjer.</span>
        <Button variant="outline" size="sm" asChild><Link to="/passord/innstillinger" data-testid="breach-enable-link"><Settings2 className="h-4 w-4 mr-1.5" /> Slå på i innstillinger</Link></Button>
      </div>
    );
  }
  if (breach.status === "running") {
    const p = breach.progress;
    return (
      <div className={cn(base, "bg-secondary/40")} data-testid="breach-status" data-state="running" aria-live="polite">
        <span className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin text-primary" /> Sjekker passordene anonymt mot kjente datalekkasjer …{p?.total ? <span className="tnum">{p.done}/{p.total}</span> : null}</span>
      </div>
    );
  }
  if (breach.status === "error") {
    return (
      <div className={cn(base, "border-warn/40")} data-testid="breach-status" data-state="error">
        <span className="flex items-center gap-2 text-warn"><AlertTriangle className="h-4 w-4" /> Lekkasjesjekken kunne ikke fullføres: {breach.error}</span>
        <Button variant="outline" size="sm" onClick={onRerun} data-testid="breach-rerun-button"><RefreshCw className="h-4 w-4 mr-1.5" /> Prøv igjen</Button>
      </div>
    );
  }
  if (breach.status === "done") {
    return (
      <div className={cn(base, breach.leaked ? "border-flag/40" : "border-success/40")} data-testid="breach-status" data-state="done">
        <span className={cn("flex items-center gap-2", breach.leaked ? "text-flag" : "text-success")}>
          {breach.leaked ? <ShieldAlert className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
          <span data-testid="breach-summary">
            {breach.leaked ? `${breach.leaked} av ${breach.checked} passord er funnet i kjente datalekkasjer.` : `Ingen av ${breach.checked} passord er funnet i kjente datalekkasjer.`}
          </span>
          <span className="text-muted-foreground text-xs">· sjekket {formatDateTime(breach.checkedAt)}</span>
        </span>
        <Button variant="ghost" size="sm" onClick={onRerun} data-testid="breach-rerun-button"><RefreshCw className="h-4 w-4 mr-1.5" /> Sjekk igjen</Button>
      </div>
    );
  }
  return null;
};

export default function HealthPage() {
  const { status, entries, refreshMeta, error, breach, runBreachCheck } = useVault();
  const [filter, setFilter] = useState("alle");
  const [sheet, setSheet] = useState({ open: false, entry: null });
  const report = useMemo(() => analyzeVault(entries, breach.results), [entries, breach.results]);

  if (status === "loading") return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-72" /></div>;
  if (status === "error") return <EmptyState title="Kunne ikke laste hvelvet" description={error} actionLabel="Prøv igjen" onAction={refreshMeta} />;
  if (status === "none") return (
    <div className="space-y-6">
      <PageHeader eyebrow="Passord" title="Passordhelse" description="Opprett hvelvet først – så analyserer vi passordene dine lokalt." />
      <EmptyState icon={Lock} title="Hvelvet er ikke opprettet" description="Sett opp passordhvelvet for å få en helseoversikt." actionLabel="Gå til hvelvet" actionTo="/passord" testId="health-no-vault" />
    </div>
  );
  if (status === "locked") return (
    <div className="space-y-6">
      <PageHeader eyebrow="Passord" title="Passordhelse" description="Lås opp hvelvet for å analysere passordene – alt skjer lokalt." />
      <UnlockScreen />
    </div>
  );

  const visible = filter === "alle" ? report.flagged : report.flagged.filter((i) => i.issues.includes(filter));

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Passord"
        title="Passordhelse"
        description={report.total ? `${report.total} ${report.total === 1 ? "oppføring" : "oppføringer"} analysert · ${report.healthy} uten funn.` : "Legg inn passord i hvelvet, så får du en helseoversikt her."}
        actions={<Button variant="outline" asChild><Link to="/passord" data-testid="health-to-vault"><KeyRound className="h-4 w-4 mr-2" /> Til hvelvet</Link></Button>}
      />

      {report.total === 0 ? (
        <EmptyState icon={HeartPulse} title="Ingenting å analysere ennå" description="Når du har lagt inn pålogginger, viser vi svake, gjenbrukte og gamle passord her." actionLabel="Legg til passord" actionTo="/passord?ny=1" testId="health-empty" />
      ) : (
        <>
          <Card>
            <CardContent className="p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <ScoreRing score={report.score} tone={report.tone} label={report.label} />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:w-[680px]">
                <StatCard icon={ShieldCheck} label="Alle funn" value={report.flagged.length} hint="bør ses på" active={filter === "alle"} onClick={() => setFilter("alle")} testId="health-filter-alle" tone="text-primary" />
                <StatCard icon={Radar} label="Lekkede" value={report.counts.leaked} hint={breach.status === "running" ? "sjekker …" : breach.status === "disabled" ? "sjekk avslått" : "i datalekkasjer"} active={filter === "leaked"} onClick={() => setFilter("leaked")} testId="health-filter-leaked" tone="text-flag" />
                <StatCard icon={ShieldAlert} label="Svake" value={report.counts.weak} hint="lett å knekke" active={filter === "weak"} onClick={() => setFilter("weak")} testId="health-filter-weak" tone="text-flag" />
                <StatCard icon={Repeat} label="Gjenbrukte" value={report.counts.reused} hint="brukt flere steder" active={filter === "reused"} onClick={() => setFilter("reused")} testId="health-filter-reused" tone="text-flag" />
                <StatCard icon={CalendarClock} label="Gamle" value={report.counts.old} hint="over 1 år" active={filter === "old"} onClick={() => setFilter("old")} testId="health-filter-old" tone="text-warn" />
              </div>
            </CardContent>
          </Card>

          <BreachStatus breach={breach} onRerun={() => runBreachCheck(true)} />

          {visible.length === 0 ? (
            <EmptyState icon={ShieldCheck} title={filter === "alle" ? "Alt ser bra ut" : "Ingen funn i denne kategorien"} description={filter === "alle" ? "Ingen lekkede, svake, gjenbrukte eller gamle passord. Fortsett slik!" : "Velg «Alle funn» for å se resten."} actionLabel={filter === "alle" ? undefined : "Vis alle funn"} onAction={filter === "alle" ? undefined : () => setFilter("alle")} testId="health-all-good" />
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Sortert etter hva som bør byttes først.</p>
              <ul className="space-y-2" data-testid="health-list">
                {visible.map(({ entry, issues, strength, ageDays, breachCount }) => (
                  <li key={entry.id} className="rounded-xl border bg-card p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`health-item-${entry.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium truncate">{entry.title || "(uten navn)"}</p>
                        {issues.map((k) => (
                          <Badge key={k} variant="outline" className={cn("border", k === "leaked" || k === "weak" || k === "reused" ? "status-avvist" : k === "old" ? "status-forfaller_snart" : "status-ma_kontrolleres")} data-testid={`health-badge-${k}`}>{k === "leaked" ? <Radar className="h-3 w-3 mr-1" /> : null}{ISSUE_META[k].label}</Badge>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {[entry.username, hostOf(entry.url)].filter(Boolean).join(" · ") || "Ingen brukernavn"}
                        {breachCount ? ` · sett ${formatBreachCount(breachCount)} ganger i lekkasjer` : ""}
                        {strength ? ` · styrke: ${strength.label.toLowerCase()}` : ""}
                        {ageDays !== null && issues.includes("old") ? ` · ${ageDays} dager siden bytte` : ""}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{ISSUE_META[issues[0]].description}</p>
                    </div>
                    <Button size="sm" onClick={() => setSheet({ open: true, entry })} data-testid={`health-fix-${entry.id}`}>
                      Bytt passord <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      <EntrySheet open={sheet.open} onOpenChange={(o) => setSheet((s) => ({ ...s, open: o }))} entry={sheet.entry} />
    </div>
  );
}
